import React, { useState, useCallback, useMemo } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { t } from "@bookingcom/lingojs-core";
import { useI18n } from "@bookingcom/lingojs-react";
import { Tab, Text, Popover, useTheme, Icon, Stack, Card } from "@bookingcom/bui-react";
import { InfoSignIcon } from "@bookingcom/bui-assets-react/streamline";
import { actions as searchActions } from "../../../store/search/actions.js";
import { useActions } from "../../../store/index.js";
import useEventTracking from "../../../hooks/useEventTracking.js";
import useCreateUrl from "../../../hooks/useCreateUrl.js";
import useGlobalContext from "../../../hooks/useGlobalContext.js";
import styles from "./SearchTabs.module.css";
const SearchTabs = ({ showBestRankingInfo, onChange, children, fill }) => {
    const history = useHistory();
    const location = useLocation();
    const i18n = useI18n();
    const actions = useActions(searchActions);
    const { requestId } = useGlobalContext();
    const trackV2 = useEventTracking("search_results", requestId);
    const theme = useTheme();
    const { enhanceURLSearchParams } = useCreateUrl();
    const initialSort = useMemo(() => {
        const query = new URLSearchParams(location.search);
        return query.get("sort") || "BEST";
    }, []);
    const [state, setState] = useState(initialSort);
    const selectTab = useCallback((sortingOrder) => {
        setState(sortingOrder);
        actions.setSortOrder(sortingOrder);
        setTimeout(function () {
            const params = new URLSearchParams(location.search);
            params.set("sort", sortingOrder);
            params.delete("page");
            params.delete("auto_load_page");
            enhanceURLSearchParams(params);
            history.replace({
                pathname: location.pathname,
                search: params.toString()
            }, { hideLoader: true });
        });
        onChange && onChange(sortingOrder);
        trackV2("click_sort_by_link", sortingOrder);
    }, [actions, history, location, enhanceURLSearchParams, onChange, trackV2]);
    const bestTitle = i18n.trans(t("flights_search_sort_best"));
    const bestTooltip = i18n.trans(t("flights_search_sort_best_tooltip"));
    const bestPopoverTriggerRef = React.useRef(null);
    const triggerList = [
        {
            id: "BEST",
            linkAttributes: {
                id: "TAB-BEST",
                "data-testid": "search_tabs_BEST",
                "aria-label": `${bestTitle} - ${bestTooltip}`
            },
            text: (React.createElement(Stack, { direction: "row" },
                React.createElement(Text, { variant: "body_2" }, bestTitle),
                React.createElement(Icon, { svg: InfoSignIcon, size: "small", color: "neutral_alt", className: styles.icon })))
        },
        {
            id: "CHEAPEST",
            linkAttributes: {
                id: "TAB-CHEAPEST",
                "data-testid": "search_tabs_CHEAPEST",
                "aria-label": i18n.trans(t("flights_search_sort_cheapest"))
            },
            text: i18n.trans(t("flights_search_sort_cheapest"))
        },
        {
            id: "FASTEST",
            linkAttributes: {
                id: "TAB-FASTEST",
                "data-testid": "search_tabs_FASTEST",
                "aria-label": i18n.trans(t("flights_search_sort_fastest"))
            },
            text: i18n.trans(t("flights_search_sort_fastest"))
        }
    ];
    return (React.createElement("div", null,
        showBestRankingInfo && (React.createElement(Popover, { triggerRef: bestPopoverTriggerRef, triggerType: "hover", hideArrow: true },
            React.createElement(Popover.Content, { attributes: { style: { maxWidth: 320, marginTop: theme.units.spacing_2x } } },
                React.createElement(Text, { variant: "body_2" }, bestTooltip)))),
        React.createElement(Tab, { onTabChange: (id) => selectTab(id), activeTabId: state, fillEqually: true },
            React.createElement(Card, { attributes: fill ? undefined : { style: { marginBottom: "var(--bui_spacing_4x)" } }, fill: true },
                React.createElement(Tab.TriggerList, null, triggerList.map(({ id, text, linkAttributes }) => (React.createElement(Tab.Trigger, { key: id, id: id, text: text, linkAttributes: linkAttributes, ...(id === "BEST" ? { ref: bestPopoverTriggerRef } : {}) }))))),
            triggerList.map(({ id }) => (React.createElement(Tab.Panel, { key: id, id: id, attributes: { "aria-labelledby": `TAB-${id}`, tabIndex: 0 } }, children))))));
};
export default SearchTabs;
